// The billing family's wire as a root's admin speaks it
// (02-DESIGN/18-billing/01-the-billing-service.md § the catalogue, § the
// subscription, § billing details and VAT, § the ledger and the export,
// § the verbs; 02-the-billing-page.md; decision 0073): the family's
// subjects in their tokenless client form, the requests and replies, the
// refusal codes, the states, kinds and treatments, and a client that speaks
// every tenant verb sealed. Mirrors impire-sdk-go/billing; JSON field names
// are the Go file's one to one, and a shape that differs is a defect here.
//
// A client, never a service: the operator's verbs
// (`billing.platform.subscriptions.list`, `…charges.export`,
// `…charges.invoiced`) are the platform form, spoken by the operator binary
// on the platform account, and are left out, as are the service's filters,
// its bucket, its vault paths and the Mollie credential's document.
import { headers as newHeaders } from "@nats-io/nats-core";
import { ServiceError } from "./envelope.js";
import { CARRIER_HEADER } from "./identity.js";
import { request } from "./request.js";
import { client, Family, validSlug } from "./subject.js";
/** SERVICE is the billing service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "billing";
/** The tokens after the family. */
export const Verb = {
    catalogue: "catalogue",
    subscription: "subscription",
    details: "details",
    change: "change",
    method: "method",
    retry: "retry",
    cancel: "cancel",
    charges: "charges",
};
/** The actions after a verb. */
export const Action = {
    get: "get",
    put: "put",
    preview: "preview",
    apply: "apply",
    change: "change",
    undo: "undo",
    list: "list",
};
/** `impire.billing.catalogue.get`: the catalogue as the page draws it. */
export function catalogueGetSubject() {
    return client(Family.billing, Verb.catalogue, Action.get);
}
/** `impire.billing.subscription.get`: the root's subscription, or none on the trial. */
export function subscriptionGetSubject() {
    return client(Family.billing, Verb.subscription, Action.get);
}
/** `impire.billing.details.put`: the billing details, the VAT number checked before the reply. */
export function detailsPutSubject() {
    return client(Family.billing, Verb.details, Action.put);
}
/** `impire.billing.change.preview`: a plan and add-on quantities, priced. */
export function changePreviewSubject() {
    return client(Family.billing, Verb.change, Action.preview);
}
/** `impire.billing.change.apply`: the preview's hash, applied. */
export function changeApplySubject() {
    return client(Family.billing, Verb.change, Action.apply);
}
/** `impire.billing.method.change`: a checkout URL for a new payment method. */
export function methodChangeSubject() {
    return client(Family.billing, Verb.method, Action.change);
}
/** `impire.billing.retry`: *Try again* on a past-due subscription's failed charge. */
export function retrySubject() {
    return client(Family.billing, Verb.retry);
}
/** `impire.billing.cancel`: the cancel at the cycle's end. */
export function cancelSubject() {
    return client(Family.billing, Verb.cancel);
}
/** `impire.billing.cancel.undo`: the cancel's undo before the cycle ends. */
export function cancelUndoSubject() {
    return client(Family.billing, Verb.cancel, Action.undo);
}
/** `impire.billing.charges.list`: the root's charges, newest first, a page at a time. */
export function chargesListSubject() {
    return client(Family.billing, Verb.charges, Action.list);
}
/**
 * Refusal codes (§ the verbs). The pattern's `bad-request`, `failed` and
 * `unauthorized` are reused by name; `not-a-root` is the identity family's,
 * its message naming the root (see rootOf).
 */
export const Code = {
    badRequest: "bad-request",
    failed: "failed",
    unauthorized: "unauthorized",
    notARoot: "not-a-root",
    providerUnavailable: "provider-unavailable",
    vatInvalid: "vat-invalid",
    vatRequired: "vat-required",
    viesUnavailable: "vies-unavailable",
    previewStale: "preview-stale",
    detailsRequired: "details-required",
    pastDue: "past-due",
};
/** VAT treatments (§ billing details and VAT). */
export const Treatment = {
    domestic: "domestic",
    reverseCharge: "reverse-charge",
    export: "export",
};
/** Subscription states (§ the subscription). */
export const State = {
    active: "active",
    pastDue: "past-due",
    ending: "ending",
    ended: "ended",
};
/** Why a subscription ended. */
export const EndedReason = {
    cancelled: "cancelled",
    pastDue: "past-due for two cycles",
};
/** Charge kinds (§ the ledger). */
export const Kind = {
    cycle: "cycle",
    proration: "proration",
    retry: "retry",
    first: "first",
};
/** The ledger's charge statuses. */
export const ChargeStatus = {
    /** Written; the payment not yet created or not yet answered. */
    open: "open",
    /** A debit on its way: Mollie's open, pending or authorized. */
    pending: "pending",
    paid: "paid",
    /** Mollie's failed, expired or canceled. */
    failed: "failed",
    /** Written on a paid charge after a refund. */
    refunded: "refunded",
    chargedBack: "charged-back",
};
/** A preview's direction. */
export const Direction = {
    /** Charged now, applied now. */
    up: "up",
    /** Written as next, applied at the next cycle. */
    down: "down",
    /** Nothing differs. */
    none: "none",
};
/** A line's kind. */
export const LineKind = {
    plan: "plan",
    addOn: "add-on",
};
/** A payment's sequence: the first sets up the payment method, a recurring one is charged on it. */
export const Sequence = {
    first: "first",
    recurring: "recurring",
};
/** The one currency and the one cycle the catalogue sells in. */
export const CURRENCY_EUR = "EUR";
export const CYCLE_MONTH = "month";
/** A page of charges' bounds. */
export const DEFAULT_CHARGES_LIMIT = 20;
export const MAX_CHARGES_LIMIT = 100;
// ---- reading a refusal ----
/** NOT_A_ROOT_PREFIX opens a not-a-root refusal's message; the root's slug follows. */
export const NOT_A_ROOT_PREFIX = "not a root: billed through ";
/**
 * rootOf reads the root's slug out of a not-a-root refusal — its message is
 * `not a root: billed through <slug>` — or "" for any other error, as the Go
 * library's RootOf.
 */
export function rootOf(err) {
    if (!(err instanceof ServiceError) || err.code !== Code.notARoot) {
        return "";
    }
    if (!err.message.startsWith(NOT_A_ROOT_PREFIX)) {
        return "";
    }
    const root = err.message.slice(NOT_A_ROOT_PREFIX.length).trim();
    return validSlug(root) ? root : "";
}
// ---- the client ----
/** DEFAULT_TIMEOUT is a call's bound; LONG_TIMEOUT a preview's and an apply's, which may wait on the provider. */
export const DEFAULT_TIMEOUT = 10_000;
export const LONG_TIMEOUT = 30_000;
/**
 * Client speaks the family's tenant verbs for one admin of a root, every
 * request and reply sealed toward the service. A refusal is a ServiceError
 * whose code is one of Code.
 */
export class Client {
    nc;
    caller;
    carrier;
    timeout;
    constructor(nc, caller, opts = {}) {
        this.nc = nc;
        this.caller = caller;
        this.carrier = opts.carrier ?? "";
        this.timeout = opts.timeout ?? DEFAULT_TIMEOUT;
    }
    opts(long = false) {
        const out = { timeout: long ? Math.max(this.timeout, LONG_TIMEOUT) : this.timeout };
        if (this.carrier !== "") {
            const h = newHeaders();
            h.set(CARRIER_HEADER, this.carrier);
            out.headers = h;
        }
        return out;
    }
    call(subj, body, long = false) {
        return request(this.nc, this.caller, SERVICE, subj, body, this.opts(long));
    }
    /** catalogue reads the plans and add-ons, with the rate the caller's details attract where they exist. */
    catalogue() {
        return this.call(catalogueGetSubject());
    }
    /** subscription reads the root's subscription, or the trial's end where it has none. */
    subscription() {
        return this.call(subscriptionGetSubject());
    }
    /** putDetails saves the billing details; refused vat-invalid, vat-required or vies-unavailable. */
    putDetails(req) {
        return this.call(detailsPutSubject(), req, true);
    }
    /** preview prices a change. */
    preview(change) {
        return this.call(changePreviewSubject(), change, true);
    }
    /** apply applies the previewed change; refused preview-stale, details-required or past-due. */
    apply(hash) {
        const body = { hash };
        return this.call(changeApplySubject(), body, true);
    }
    /** changeMethod answers a checkout URL for a new payment method. */
    changeMethod() {
        return this.call(methodChangeSubject(), undefined, true);
    }
    /** retry tries the failed charge again. */
    retry() {
        return this.call(retrySubject(), undefined, true);
    }
    /** cancel ends the subscription at the cycle's end. */
    cancel() {
        return this.call(cancelSubject());
    }
    /** undoCancel takes the cancel back before the cycle ends. */
    undoCancel() {
        return this.call(cancelUndoSubject());
    }
    /** charges reads one page of the root's charges, newest first. */
    charges(req = {}) {
        return this.call(chargesListSubject(), req);
    }
}
//# sourceMappingURL=billing.js.map